<template>
  <div class="eula-container d-flex align-center justify-center">
    <v-card max-width="800" class="pa-6 eula-card">
      <h2 class="font-weight-medium mb-4">AVA End User License Agreement</h2>
      <v-divider></v-divider>
      
      <!-- Acceptance Instructions -->
      <div class="acceptance-text pa-4 my-4">
        <p class="text-body-1 mb-3">
          By checking this box and clicking "Accept", you confirm that you have read, understood, and agree to be legally bound by the AVA End User License Agreement (EULA) on behalf of your organization.
        </p>
        <p class="text-body-2 mb-4">
          If you do not agree to these terms, you must click "Decline" and discontinue use of the software.
        </p>
      </div>

      <!-- View Full EULA Link -->
      <div class="mb-4">
        <a 
          href="#" 
          class="eula-link" 
          @click.prevent="showEulaModal = true"
        >
          View Full EULA
        </a>
      </div>

      <!-- Acceptance Checkbox -->
      <v-checkbox
        hide-details
        v-model="isChecked"
        class="mt-2"
        :label="'I have read and accept the AVA End User License Agreement.'"
      ></v-checkbox>

      <!-- Action Buttons -->
      <v-row justify="center" class="mt-6">
        <v-btn
          color="primary"
          class="ma-2"
          :disabled="!isChecked"
          :loading="loading"
          @click="accept"
        >
          Accept
        </v-btn>
        <v-btn color="error" class="ma-2" @click="decline">Decline</v-btn>
      </v-row>

    </v-card>

    <!-- Full EULA Modal -->
    <v-dialog
      v-model="showEulaModal"
      max-width="900"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">AVA End-User License Agreement</span>
          <v-btn icon @click="showEulaModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        
        <v-card-text style="height: 600px;">
          <div class="eula-text">
            <div class="text-center my-6">
              <h2 class="text-h6 font-weight-bold">AVA End-User License Agreement (EULA)</h2>
            </div>
            
            <p class="mb-4">
              This End-User License Agreement ("Agreement") is a legal agreement between you (the "User") and Envizion Ltd ("Licensor"), the owner and provider of the AVA software platform ("Software"), whether accessed directly from Envizion Ltd or via an authorized AVA Partner. By accessing or using the Software, you agree to be bound by this Agreement. If you do not agree, you must not use the Software.
            </p>

            <h3 class="eula-heading">1. License Grant</h3>
            <p class="mb-4">
              Envizion Ltd grants you a non-exclusive, non-transferable, revocable license to access and use the Software solely for your employer's internal business purposes, in accordance with this Agreement and any terms set by your AVA Partner or Envizion Ltd.
            </p>

            <h3 class="eula-heading">2. Intellectual Property Rights</h3>
            <p class="mb-4">
              All intellectual property rights in the Software and all related components remain the sole and exclusive property of Envizion Ltd. This Agreement does not transfer any ownership rights to the User.
            </p>

            <h3 class="eula-heading">3. Use Restrictions</h3>
            <p class="mb-2">You agree to use the Software only as permitted:</p>
            <ul class="mb-4">
              <li>Use is limited to devices and systems under your direct control.</li>
              <li>You may not install, access, or transfer the Software to unauthorized environments.</li>
              <li>You may not:
                <ul class="ml-4 mt-2">
                  <li>Use the Software for commercial services such as training or bureau offerings;</li>
                  <li>Exceed the number of licensed users;</li>
                  <li>Copy, distribute, lease, sublicense, or provide third-party access;</li>
                  <li>Modify, merge, or combine the Software with other programs;</li>
                  <li>Remove or alter trademarks, copyright notices, or branding;</li>
                  <li>Reverse-engineer or attempt to extract the source code;</li>
                  <li>Bypass or interfere with security or access controls.</li>
                </ul>
              </li>
            </ul>

            <h3 class="eula-heading">4. Prohibited Use</h3>
            <p class="mb-4">
              You must not use the Software to store, transmit, or publish content that is unlawful, defamatory, harassing, offensive, or discriminatory. You must not use it to facilitate illegal activity or distribute malicious content such as viruses or malware.
            </p>

            <h3 class="eula-heading">5. Data Ownership and Hosting</h3>
            <p class="mb-4">
              All data uploaded to or generated using the Software—including but not limited to 360° images, point clouds, models, and documents—remains under the control of the User or their authorized AVA Partner. Envizion Ltd does not claim ownership of user-generated data.
            </p>

            <h3 class="eula-heading">6. Support and Maintenance</h3>
            <p class="mb-4">
              Day-to-day support will be provided by your AVA Partner. Platform-level technical support will be provided by Envizion Ltd in line with partner agreements. All support requests should be submitted via the AVA portal.
            </p>

            <h3 class="eula-heading">7. Fair Usage</h3>
            <p class="mb-4">
              Advanced features (e.g., AI processing, model automation, analytics) may be subject to fair usage thresholds. Envizion Ltd reserves the right to restrict access if usage is excessive or affects system performance.
            </p>

            <h3 class="eula-heading">8. Transition to Direct Licensing</h3>
            <p class="mb-4">
              If a User transitions from Partner-managed access to a direct license with Envizion Ltd, the original Partner may receive a share of license revenue for a defined period. This will not affect User access or pricing.
            </p>

            <h3 class="eula-heading">9. Termination</h3>
            <p class="mb-4">
              This Agreement terminates automatically upon any breach. Upon termination, you must stop using the Software, remove it from all systems, and confirm removal if requested.
            </p>

            <h3 class="eula-heading">10. Disclaimer of Warranties</h3>
            <p class="mb-4">
              The Software is provided by Envizion Ltd "as is", with no warranties—express or implied—including merchantability, fitness for purpose, or non-infringement.
            </p>

            <h3 class="eula-heading">11. Limitation of Liability</h3>
            <p class="mb-4">
              To the extent permitted by law, Envizion Ltd shall not be liable for indirect, incidental, or consequential damages (including loss of profits or data). Total liability is limited to the amount paid for Software use in the previous 12 months.
            </p>

            <h3 class="eula-heading">12. Governing Law</h3>
            <p class="mb-4">
              This Agreement shall be governed by and interpreted in accordance with the laws of Scotland, and any disputes shall be subject to the exclusive jurisdiction of the Scottish courts.
            </p>
          </div>

          <div class="text-center py-4">
            <img 
              src="/img/Technology By.png" 
              alt="Technology By Envizion" 
              class="technology-logo"
            />
          </div>
        </v-card-text>

        <v-divider></v-divider>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showEulaModal = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'EulaView',
  data() {
    return {
      loading: false,
      isChecked: false,
      showEulaModal: false,
    };
  },
  methods: {
    ...mapActions('ACCOUNT', ['ACCEPT_EULA', 'LOGOUT']),
    async accept() {
      try {
        this.loading = true;
        await this.ACCEPT_EULA();
        // Wait for Vue reactivity to update and then redirect
        await this.$nextTick();
        const redirectTo = this.$route.query.redirect || '/';
        this.$router.replace(redirectTo).catch(() => {});
      } finally {
        this.loading = false;
      }
    },
    decline() {
      this.LOGOUT();
    },
  },
};
</script>

<style lang="scss" scoped>
.eula-container {
  position: relative;
  height: 100vh;
  width: 100vw;
  background-image: url('../../public/img/Envizion-login-BG.webp');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
}

/* Fade overlay */
.eula-container::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6); /* Adjust opacity as needed */
  pointer-events: none;
}

/* Ensure card is above overlay */
.eula-card {
  position: relative;
  z-index: 1;
}

.eula-heading {
  font-weight: 600;
  margin-top: 1.5rem;
  color: #1976d2;
}

.acceptance-text {
  background-color: #f5f5f5;
  border-radius: 4px;
  border-left: 4px solid #1976d2;
}

.eula-link {
  color: #1976d2;
  text-decoration: underline;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    color: #1565c0;
    text-decoration: none;
  }
}

.eula-text {
  font-size: 14px;
  line-height: 1.6;
  
  h3 {
    color: #1976d2;
    font-weight: 600;
    margin-top: 2rem;
  }
  
  ul {
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
  
  p {
    text-align: justify;
  }
}

.technology-logo {
  max-height: 60px;
  max-width: 100%;
  opacity: 0.8;
  filter: grayscale(20%);
}

.technology-logo-small {
  max-height: 40px;
  max-width: 100%;
  opacity: 0.7;
  filter: grayscale(30%);
}
</style> 